import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { colonyApi } from "@/lib/colonyApi";
import { formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { CheckCircle2, Home } from "lucide-react";
import { useColonyBrand } from "./useColonyBrand";

/**
 * Colony-branded invite acceptance page. Backend re-uses the shared
 * /auth/reset-password endpoint — same tokens work for invites and
 * password resets so we don't need a separate flow.
 */
export default function ColonyAcceptInvite() {
  useColonyBrand();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (!token) return toast.error("Missing token — please open the link from your email again.");
    setBusy(true);
    try {
      await colonyApi.post("/colony/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/managementusa"), 2000);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-background border border-border rounded-sm p-8">
        <div className="flex items-baseline gap-2 mb-6">
          <Home className="w-5 h-5 text-emerald-700" />
          <span className="text-xl font-black tracking-tighter text-emerald-800 font-display">BUNGALOW</span>
          <span className="text-sm font-light tracking-wide">PORTAL</span>
        </div>
        <h1 className="font-display text-3xl font-black tracking-tighter">Activate your account</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-6">
          Pick a password to finish setting up your colony portal login.
        </p>
        {done ? (
          <div className="border border-emerald-500/40 bg-emerald-50 rounded-sm p-4 text-sm" data-testid="colony-invite-done">
            <div className="flex items-center gap-2 font-semibold text-emerald-700">
              <CheckCircle2 className="w-4 h-4" /> Account activated
            </div>
            <p className="text-muted-foreground mt-1">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>New password</Label>
              <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)}
                className="rounded-sm h-11" autoFocus minLength={6}
                inputTestId="colony-invite-password" testId="colony-invite-password-toggle" />
            </div>
            <div>
              <Label>Confirm password</Label>
              <PasswordInput required value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="rounded-sm h-11" minLength={6}
                inputTestId="colony-invite-password2" testId="colony-invite-password2-toggle" />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="colony-invite-submit"
            >
              {busy ? "Setting password…" : "Activate account"}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              <Link to="/managementusa" className="underline hover:text-foreground">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
